"use strict";
/**
 * This module provides access to an EVA Service call and ensures all required
 * settings, headers and call conditions are met
 *
 * @preferred
 */
Object.defineProperty(exports, "__esModule", { value: true });
var settings_1 = require("./settings");
var xhr_1 = require("./xhr");
var service_cache_1 = require("./service-cache");
var _ = require("lodash");
/**
 * Wrapper class for a call to the EVA back-end
 * Ensure all proper settings are applied and handles all the type casting of
 * request and response for the XMLHttpRequest calls
 * @type {class}
 */
var EVAService = /** @class */ (function () {
    /**
     * Constructor for the EVAService class
     * @param  {string} name    The name of the EVA service to call
     * @return {void}           This method returns nothing
     */
    function EVAService(name, path, method) {
        if (method === void 0) { method = xhr_1.EXMLHttpRequestMethod.POST; }
        this.name = name || "GenericService";
        this.path = path;
        this.method = method;
    }
    /**
     * Convenience method to get the current session ID
     * @return {string} The current sessionID
     */
    EVAService.prototype.getSessionID = function () {
        return settings_1.SettingsManager.getSessionId();
    };
    /**
     * Performs the actual call to the backend
     * @param  {REQ}        requestData The data to be sent as the body for this call
     * @return {Promise}                Return a promise that will resolve with the call response
     */
    EVAService.prototype.call = function (requestData, disableCache, timeout) {
        var _this = this;
        var serviceName = this.name;
        var callPromise;
        // Check for a cached service call response (unless explicitly disabled)
        // If a path has been defined we will only allow caching of GET requests
        // If it's a message style request we will only allow caching of GetXYZ and ListXYZ calls
        //
        var restGetCall = (this.path !== undefined && this.method === xhr_1.EXMLHttpRequestMethod.GET);
        var messageGetCall = (this.path === undefined && serviceName.match(/^Get|^List/) !== null);
        if (disableCache !== true && (restGetCall || messageGetCall)) {
            callPromise = service_cache_1.serviceCache.get(serviceName, requestData, this.method, this.path);
            if (callPromise) {
                // Return from cache
                //
                return callPromise;
            }
        }
        callPromise = new Promise(function (resolve, reject) {
            var headers = {};
            // Add user token if set
            //
            var userToken = settings_1.SettingsManager.getAuthenticationToken();
            if (typeof userToken !== "undefined" && userToken !== null) {
                headers.Authorization = userToken;
            }
            // Add application token if set
            //
            var applicationToken = settings_1.SettingsManager.getApplicationToken();
            if (applicationToken) {
                headers["EVA-App-Token"] = applicationToken;
            }
            // Add administrative headers
            //
            headers.Accept = "application/json, text/javascript";
            headers.Authorization = settings_1.SettingsManager.getAuthenticationToken();
            headers["Accept-Language"] = settings_1.SettingsManager.getLanguage();
            headers["Content-Type"] = "application/json";
            headers.ClientVersion = settings_1.SettingsManager.getVersion();
            headers.ClientName = settings_1.SettingsManager.getClientName();
            headers["EVA-User-Agent"] = settings_1.SettingsManager.getAppName() + "/" + settings_1.SettingsManager.getAppVersion();
            // Prepare the request body
            // Make a copy of the request data or we mess up the service cache
            //
            var requestBody = _.clone(requestData) || {};
            // Add session ID if not provided
            //
            if (!requestBody.SessionID) {
                requestBody.SessionID = settings_1.SettingsManager.getSessionId();
            }
            var callURL = settings_1.SettingsManager.getEndPointURL() + (_this.path || "/message/" + serviceName);
            var xhr = new xhr_1.XHR();
            // Override default timeout if requested
            //
            if (_.isNumber(timeout)) {
                xhr.setTimeout(Math.max(0, timeout));
            }
            // console.log( "[XHR] Performing call", callURL, requestBody, this.method );
            xhr.call({
                method: _this.method,
                url: callURL,
                contentType: "application/json",
                headers: headers,
                data: requestBody,
            })
                .then(function (xhrResponse) {
                // console.log( "[Service:" + serviceName + "] Service call completed. Status code:", xhrResponse.status );
                // Build the response structure ensure all types are cast correctly
                //
                var serviceResponse = {
                    status: xhrResponse.status,
                    request: requestData,
                    response: xhrResponse.response,
                    xhr: xhrResponse.xhr
                };
                // Check the response headers for a new EVA application token
                //
                var newApplicationToken = xhrResponse.xhr.getResponseHeader("EVA-App-Token");
                if (newApplicationToken) {
                    settings_1.SettingsManager.setApplicationToken(newApplicationToken);
                }
                var evaWarnings = xhrResponse.xhr.getResponseHeader("EVA-Warning");
                if (evaWarnings) {
                    var warnings = evaWarnings.split(", ");
                    _.each(warnings, function (warning) {
                        console.warn("[EVA-WARNING]", warning);
                    });
                }
                // Check the message response and determine if this is a success or failure
                // Using the xhrResponse here because it is cast as 'any'
                //
                if (xhrResponse.response.Error || xhrResponse.response.Exception) {
                    reject(serviceResponse);
                }
                else {
                    resolve(serviceResponse);
                }
            })
                .catch(function (error) {
                // console.error( "[Service:" + serviceName + "] Service call error", error.status );
                reject(error);
            });
        });
        // If the serviceName starts with one of the following we will need to purge the cache
        //
        // * Create
        // * Store
        // * Add
        // * Modify
        // * Cancel
        // * Delete
        // * Detach
        // * Attach
        //
        if (serviceName.match(/^Create|^Store|^Add|^Modify|^Cancel|^Delete|^Detach|^Attach/) !== null) {
            service_cache_1.serviceCache.flush();
        }
        else {
            service_cache_1.serviceCache.add(serviceName, requestData, this.method, this.path, callPromise);
        }
        return callPromise;
    };
    return EVAService;
}());
exports.EVAService = EVAService;
